import React, { Component } from "react";
import PropTypes from "prop-types";
import { graphql, withApollo } from "@apollo/client/react/hoc";
import { gql } from "@apollo/client";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { connect } from "react-redux";
import axios from "axios";
import YAML from "yaml";
import { injectIntl } from "react-intl";
import { Navigate } from "react-router-dom";

import ErrorFocus from "./elements/error-focus";
import LoadingIndicator from "../../../../general-components/loading-indicator";
import webformQuery from "./webform.graphql";
import submitMutation from "./submitMutation.graphql";
import { restHost } from "../../../../config";
import WebformElements from "./webform-elements";
import { states } from "./states";
import { getItemCompositeAddressElements } from "./elements/composite-address";

import * as Sentry from "@sentry/react";
import toast from "@pro/js/components/ui/toast";

const WEBFORM_OPTIONS_QUERY = gql`
  query QueryWebformOptions($id: ID!) {
    entityById(entityType: WEBFORM_OPTIONS, id: $id) {
      ... on WebformOptions {
        options
      }
    }
  }
`;

const mapStateToProps = (reduxStore) => ({ reduxStore });
const webformElementsNotProcess = [
  "markup",
  "webform_actions",
  "hidden",
  "webform_markup",
];
export const webformElementsCustomExcluded = [
  "editor",
  "rating",
  "accepted",
  "denied",
  "badge",
  "keychange_company",
];
// @todo Add details.
const webformElementsContainer = [
  "webform_custom_composite",
  "webform_address_composite",
  "fieldset",
  "webform_flexbox",
  "container",
];
const webFormElementsNoLevel = [
  "fieldset",
  "webform_flexbox",
  "container",
  "details",
];

export const objectToArray = (object) => {
  // Respect containers.
  for (const [key, value] of Object.entries(object)) {
    if (object[key]["#type"] === "webform_custom_composite") {
      object[key]["elements"] = objectToArray(object[key]["#element"]);
      delete object[key]["#element"];

      continue;
    }
    for (const [subKey, subValue] of Object.entries(value)) {
      if (
        subValue !== null &&
        typeof subValue === "object" &&
        !subKey.startsWith("#")
      ) {
        const items = object[key]["elements"] ? object[key]["elements"] : [];

        object[key]["elements"] = [...items, { id: subKey, ...subValue }];
      }
    }
  }

  return Object.keys(object).map((key) => ({
    ...object[key],
    id: key,
  }));
};

export const getValidationSchema = (item, intl, values = null) => {
  const _t = (id, values) => intl.formatMessage({ id }, values);

  let itemSchema;
  // @todo Translation!
  switch (item["#type"]) {
    case "webform_custom_composite":
      itemSchema = yup.array();
      break;

    case "textfield":
      itemSchema = yup.string();

      if (item["#maxlength"]) {
        itemSchema = itemSchema.max(item["#maxlength"]);
      } else {
        itemSchema = itemSchema.max(255);
      }
      if (item["#minlength"]) {
        itemSchema = itemSchema.min(item["#minlength"]);
      }

      break;
    case "entity_autocomplete":
      itemSchema = yup.string();
      break;

    case "textarea":
      itemSchema = yup.string();

      if (item["#maxlength"]) {
        itemSchema = itemSchema.max(item["#maxlength"]);
      }
      if (item["#minlength"]) {
        itemSchema = itemSchema.min(item["#minlength"]);
      }

      break;

    // @todo Make date more robust.
    case "date":
      itemSchema = yup.string();
      break;

    case "url":
      itemSchema = yup.string().url(_t("webform.validation.invalid_url"));
      break;

    case "email":
      itemSchema = yup.string().email(_t("webform.validation.invalid_email"));
      break;

    case "number":
      // @todo min, max, step.
      itemSchema = yup
        .number()
        .transform((value, originalValue) => {
          if (originalValue === "") {
            return undefined;
          }

          if (String(originalValue).match(/e/i)) {
            return Number.NaN;
          }

          return value;
        })
        .typeError(({ originalValue }) =>
          String(originalValue).match(/e/i)
            ? _t("webform.validation.invalid_number_no_exponent")
            : _t("webform.validation.invalid_number")
        );
      break;

    case "managed_file":
      itemSchema = yup.string();
      break;

    case "radios":
      itemSchema = yup.string();
      break;

    case "checkbox":
      itemSchema = yup.boolean();
      break;

    case "checkboxes":
      itemSchema = yup.array();
      break;
    case "select":
    case "webform_entity_select":
    case "webform_term_select":
      itemSchema = yup.array();

      if (typeof item["#multiple"] === "undefined") {
        itemSchema = itemSchema.max(
          1,
          item["#multiple_error"] || _t("webform.validation.max_one_value")
        );
      }

      if (typeof item["#multiple"] === "number") {
        itemSchema = itemSchema.max(
          item["#multiple"],
          item["#multiple_error"] ||
            _t("webform.validation.max_value", { max: item["#multiple"] })
        );
      }

      // Select is Single by default
      if (
        !item["#multiple"] &&
        ["select", "webform_entity_select", "webform_term_select"].includes(
          item["#type"]
        )
      ) {
        itemSchema = yup.string();
      }

      break;
  }

  // Mark as not required if there are 'visible' states, in that case
  // the logic is handled via the required prop on the field...
  // ...which does not work, so the mutations returns an error which
  // is shown to the user.
  let visible = true;
  if (values) {
    visible = states(item.states, values).visible;
  }

  if (
    (item["#required"] && (item.states === null || !item.states) && !values) ||
    //(!item.states && !values) ||
    (item["#required"] && visible && values)
  ) {
    const requiredMessage =
      item["#required_error"] ||
      _t("webform.validation.required", { field: item["#title"] });

    if (item["#type"] === "checkbox") {
      itemSchema = itemSchema.oneOf([true], requiredMessage);
    } else {
      itemSchema = itemSchema.required(requiredMessage);
    }
  }

  return itemSchema;
};

const normalizeNumberValue = (value) => {
  return typeof value === "number" ? String(value) : value;
};

export const normalizeWebformSubmissionValues = (values, items) => {
  const normalizedValues = { ...values };

  const normalizeItems = (target, elements) => {
    elements.forEach((item) => {
      if (
        webformElementsNotProcess.includes(item["#type"]) ||
        webformElementsCustomExcluded.includes(item.id)
      ) {
        return;
      }

      if (item["#type"] === "number") {
        target[item.id] = normalizeNumberValue(target[item.id]);
        return;
      }

      if (!webformElementsContainer.includes(item["#type"]) || !item.elements) {
        return;
      }

      if (webFormElementsNoLevel.includes(item["#type"])) {
        normalizeItems(target, item.elements);
        return;
      }

      const itemValue = target[item.id];
      if (Array.isArray(itemValue)) {
        itemValue.forEach((childValue) => {
          if (childValue && typeof childValue === "object") {
            normalizeItems(childValue, item.elements);
          }
        });
        return;
      }

      if (itemValue && typeof itemValue === "object") {
        normalizeItems(itemValue, item.elements);
      }
    });
  };

  normalizeItems(normalizedValues, items);

  return normalizedValues;
};

const htmlEntities = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
};

const decodeHtmlEntities = (value) =>
  value.replace(/&(#\d+|#x[\da-fA-F]+|[a-zA-Z][\w]+);/g, (match, entity) => {
    if (entity[0] === "#") {
      const codePoint =
        entity[1]?.toLowerCase() === "x"
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10);

      return Number.isNaN(codePoint) || codePoint < 0 || codePoint > 0x10ffff
        ? match
        : String.fromCodePoint(codePoint);
    }

    return htmlEntities[entity] ?? match;
  });

export const formatWebformViolationMessage = (message) =>
  decodeHtmlEntities(String(message ?? "").replace(/<[^>]*>/g, ""))
    .replace(/\s+/g, " ")
    .trim();

const getWebformViolationMessage = (violation) =>
  formatWebformViolationMessage(violation?.message ?? violation);

/**
 * Used to generate the initialValues.
 *
 * @param item
 * @returns {*[]|string|{}[]|*}
 */
export const getInitialValue = (item) => {
  let subElements =
    item["#type"] === "webform_address_composite"
      ? getItemCompositeAddressElements(item)
      : item.elements;

  if (item["#type"] === "webform_address_composite") {
    if (item["#default_value"]) {
      subElements = getItemCompositeAddressElements(item).map(
        (addressElement) => {
          if (
            Object.prototype.hasOwnProperty.call(
              item["#default_value"],
              addressElement.id
            )
          ) {
            return {
              ...addressElement,
              "#default_value": !item["#multiple"]
                ? item["#default_value"][addressElement.id]
                : [item["#default_value"][addressElement.id]],
            };
          }

          return addressElement;
        }
      );
    }
  }

  if (webformElementsContainer.includes(item["#type"])) {
    // Composite or container elements.
    let containerElements = {};

    subElements.forEach((containerItem) => {
      // Do not process these element types.
      if (
        webformElementsNotProcess.includes(containerItem["#type"]) ||
        webformElementsCustomExcluded.includes(containerItem.id)
      ) {
        return;
      }

      containerElements[containerItem.id] = getInitialValue(containerItem);
    });

    return item["#type"] === "webform_address_composite" ||
      webFormElementsNoLevel.includes(item["#type"]) ||
      (item["#type"] === "webform_custom_composite" && !item["#multiple"])
      ? containerElements
      : [containerElements];
  } else {
    if (item["#default_value"]) {
      return item["#default_value"];
    } else {
      if (item["#type"] === "checkbox") {
        return false;
      }

      if (item["#type"] === "checkboxes") {
        if (
          !item["#multiple"] ||
          (item["#multiple"] && item["#multiple"].length > 1)
        ) {
          return [];
        }
        return "";
      }

      return [
        "select",
        "webform_term_select",
        "webform_entity_select",
      ].includes(item["#type"]) && item["#multiple"]
        ? []
        : "";
    }
  }
};

class ParagraphForm extends Component {
  state = {
    submitResponse: null,
    submitted: false,
    token: "",
    defaultInitialValues: this.props.defaultInitialValues,
    elements: this.props.data?.entityById?.elements
      ? objectToArray(YAML.parse(this.props.data.entityById.elements))
      : [],
    elementsYML: this.props.data?.entityById?.elements,
    redirect: false,
  };

  formContainer = React.createRef();

  async componentDidMount() {
    axios
      .get(`${restHost}/session/token`)
      .then((response) => {
        // Save Session Token to redux store.
        this.setState({
          token: response.data,
        });
      })
      .catch((error) => {
        console.error(error);
      });

    const elements = this.state.elements;

    for (let item of elements) {
      if (typeof item["#options"] === "string") {
        const id = item["#options"];
        await this.props.client
          .query({ query: WEBFORM_OPTIONS_QUERY, variables: { id: id } })
          .then((response) => {
            item["#options"] = YAML.parse(response.data?.entityById?.options);
          });
      }
    }

    this.setState({
      elements: elements,
    });
  }

  async componentDidUpdate(prevProps) {
    if (
      (prevProps.data.loading && !this.props.data.loading) ||
      this.state.elementsYML !== this.props.data.entityById?.elements
    ) {
      let elementsLanguageBased = objectToArray(
          YAML.parse(this.props.data.entityById.elements)
        ),
        elementsOriginal = objectToArray(
          YAML.parse(this.props.data.entityById.elementsOriginal)
        );

      let elements = elementsOriginal.map((t1) => ({
        ...t1,
        ...elementsLanguageBased.find((t2) => t2.id === t1.id),
      }));

      elements = elements.map((item) => {
        if (item["#placeholder"] && item["#required"]) {
          item["#placeholder"] = `${item["#placeholder"]} *`;
        }

        if (item["#empty_option"] && item["#required"]) {
          item["#empty_option"] = `${item["#empty_option"]} *`;
        }

        return item;
      });

      for (let item of elements) {
        if (typeof item["#options"] === "string") {
          const id = item["#options"];
          await this.props.client
            .query({ query: WEBFORM_OPTIONS_QUERY, variables: { id: id } })
            .then((response) => {
              item["#options"] = YAML.parse(response.data?.entityById?.options);
            });
        }
      }

      this.setState({
        elements: elements,
        elementsYML: this.props.data.entityById.elements,
      });
    }
  }

  submitForm = (values, setSubmitting) => {
    const { formatMessage } = this.props.intl;
    const normalizedValues = normalizeWebformSubmissionValues(
      values,
      this.state.elements
    );

    const webformId = this.props.content.fieldFormRawField.first.targetId;
    const formData = {
      webform_id: webformId,
      ...normalizedValues,
      sid: this.props.submissionId,
      langcode: this.props.reduxStore.i18n.currentLanguage,
    };

    if (/^akkreditierung_von_presse(?:vertret|\d{2})$/.test(webformId)) {
      formData["denied"] = "0";
      formData["accepted"] = "0";
      formData["badge"] = "0";
    }

    this.props.client
      .mutate({
        mutation: submitMutation,
        variables: {
          data: {
            values: JSON.stringify(formData),
          },
        },
      })
      .then((response) => {
        console.log(response.data);
        setSubmitting(false);

        if (this.props.packageName === "pro") {
          if (response.data.submitWebform.errors.length) {
            // Combine messages like in render into one main message and print in toast.
            const errorMessage = response.data.submitWebform.errors
              .map(getWebformViolationMessage)
              .filter(Boolean)
              .join("\n");
            toast({
              message: errorMessage,
              type: "error",
            });
          }
          if (
            response.data.submitWebform.submission &&
            !response.data.submitWebform.errors.length
          ) {
            toast({
              message: formatMessage({
                id: this.props.redirectPath
                  ? "webforms.success_redirect"
                  : "webforms.success",
              }),
              type: "success",
            });
          }
        } else {
          this.setState({
            submitResponse: response.data.submitWebform,
          });
        }

        if (response.data.submitWebform.submission?.encodedData) {
          this.setState({
            defaultInitialValues: JSON.parse(
              response.data.submitWebform.submission?.encodedData
            ),
          });
        }

        // Success.
        if (response.data.submitWebform.submission) {
          this.setState({ submitted: true });

          this.formContainer.current.scrollIntoView(true);

          if (this.props.redirectPath) {
            setTimeout(() => {
              this.setState({ redirect: true });
            }, 2500);
          }
        }

        // Error.
        if (response.data.submitWebform.errors.length) {
          Sentry.captureException(response.data.submitWebform.errors);
          this.formContainer.current.scrollIntoView(true);
        }
        if (response.errors) {
          Sentry.captureException(response.errors);
          this.formContainer.current.scrollIntoView(true);
        }
      });
  };

  render() {
    if (
      !this.props.packageName &&
      (this.props.data.loading || !this.state.elementsYML)
    ) {
      return <LoadingIndicator />;
    }

    if (
      this.props.packageName === "pro" &&
      (this.props.data.loading || !this.state.elementsYML)
    ) {
      return (
        <div className="mt-5">
          <div
            className="spinner-border"
            role="status"
            style={{ margin: "0 auto", display: "block" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (this.state.redirect) {
      return <Navigate to={this.props.redirectPath} />;
    }

    if (this.props.data.error) {
      return <div>{this.props.data.error.message}</div>;
    }

    const { formatMessage } = this.props.intl;

    let initialValues = {},
      validationSchema = {};

    this.state.elements.forEach((item) => {
      // Do not process these element types.
      if (
        webformElementsNotProcess.includes(item["#type"]) ||
        webformElementsCustomExcluded.includes(item.id)
      ) {
        return;
      }

      // Get initial values.
      if (webFormElementsNoLevel.includes(item["#type"])) {
        // For these elements, the subelements are not nested into their
        // parents.
        item.elements.forEach((rootItem) => {
          if (webFormElementsNoLevel.includes(rootItem["#type"])) {
            initialValues = {
              ...initialValues,
              ...getInitialValue(rootItem),
            };
          } else {
            initialValues = {
              ...initialValues,
              [rootItem.id]: getInitialValue(rootItem),
            };
          }
        });
      } else {
        initialValues = {
          ...initialValues,
          [item.id]: getInitialValue(item),
        };
      }

      // Validation schema.
      if (webFormElementsNoLevel.includes(item["#type"])) {
        item.elements.forEach((rootItem) => {
          validationSchema = {
            [rootItem.id]: getValidationSchema(rootItem, this.props.intl),
            ...validationSchema,
          };
        });
      } else {
        validationSchema = {
          [item.id]: getValidationSchema(item, this.props.intl),
          ...validationSchema,
        };
      }
    });

    const formParagraphDefaultData = this.props.content.fieldFormRawField.first
      .defaultData
      ? YAML.parse(this.props.content.fieldFormRawField.first.defaultData)
      : {};

    return (
      <div className={`paragraph webform-paragraph`}>
        <Formik
          initialValues={
            this.state.defaultInitialValues
              ? {
                  ...initialValues,
                  ...formParagraphDefaultData,
                  ...this.state.defaultInitialValues,
                }
              : { ...initialValues, ...formParagraphDefaultData }
          }
          validationSchema={yup.lazy((values) => {
            this.state.elements.forEach((item) => {
              // Do not process these element types.
              if (
                webformElementsNotProcess.includes(item["#type"]) ||
                webformElementsCustomExcluded.includes(item.id)
              ) {
                return;
              }

              // Validation schema.
              if (webFormElementsNoLevel.includes(item["#type"])) {
                item.elements.forEach((rootItem) => {
                  validationSchema = {
                    [rootItem.id]: getValidationSchema(
                      rootItem,
                      this.props.intl,
                      values
                    ),
                    ...validationSchema,
                  };
                });
              } else {
                validationSchema = {
                  [item.id]: getValidationSchema(item, this.props.intl, values),
                  ...validationSchema,
                };
              }
            });

            return yup.object().shape(validationSchema);
          })}
          onSubmit={(values, { setSubmitting }) =>
            this.submitForm(values, setSubmitting)
          }
        >
          {(formik) => (
            <div
              className={`container webform-container${
                this.state.submitted ? " submitted" : ""
              }`}
              ref={this.formContainer}
            >
              <div className="row">
                <div
                  className={
                    this.props.wrapperClasses ?? `col-12 offset-lg-2 col-lg-9`
                  }
                >
                  {this.state.submitResponse &&
                    !this.state.submitResponse.submission &&
                    this.state.submitResponse.errors.length && (
                      <div
                        className={`${this.props.content.fieldFormRawField.first.targetId} form-messages`}
                      >
                        <div className="alert alert-danger" role="alert">
                          {this.state.submitResponse.errors.map(
                            (item, index) => (
                              <React.Fragment key={index}>
                                <span>{getWebformViolationMessage(item)}</span>
                                <br />
                              </React.Fragment>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {this.state.submitResponse &&
                    this.state.submitResponse.submission &&
                    !this.state.submitResponse.errors.length && (
                      <div
                        className={`${this.props.content.fieldFormRawField.first.targetId} form-messages`}
                      >
                        <div className="alert alert-success" role="alert">
                          {formatMessage({
                            id: this.props.redirectPath
                              ? "webforms.success_redirect"
                              : "webforms.success",
                          })}
                        </div>
                      </div>
                    )}

                  <Form
                    noValidate
                    className={
                      this.props.content.fieldFormRawField.first.targetId
                    }
                  >
                    <fieldset disabled={this.state.submitted}>
                      <WebformElements
                        formik={formik}
                        items={this.state.elements}
                        token={this.state.token}
                        generatedInitialValues={initialValues}
                        redirectPath={this.props.redirectPath}
                        packageName={this.props.packageName}
                      />
                    </fieldset>

                    <ErrorFocus
                      isSubmitting={formik.isSubmitting}
                      errors={formik.errors}
                      isValidating={formik.isValidating}
                    />

                    {formik.isSubmitting &&
                      this.props.packageName !== "pro" && <LoadingIndicator />}
                  </Form>
                </div>
              </div>
            </div>
          )}
        </Formik>
      </div>
    );
  }
}

ParagraphForm.propTypes = {
  client: PropTypes.object.isRequired,
  content: PropTypes.shape({
    fieldFormRawField: PropTypes.shape({
      first: PropTypes.shape({
        targetId: PropTypes.string,
        defaultData: PropTypes.string,
      }),
    }),
  }),
  data: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.shape({
      message: PropTypes.string,
    }),
    entityById: PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      elements: PropTypes.string,
      elementsOriginal: PropTypes.string,
    }),
  }),
  defaultInitialValues: PropTypes.object,
  submissionId: PropTypes.string,
  reduxStore: PropTypes.object,
  intl: PropTypes.object.isRequired,
  wrapperClasses: PropTypes.string,
  packageName: PropTypes.string,
  redirectPath: PropTypes.string,
};

export default injectIntl(
  connect(mapStateToProps)(
    graphql(webformQuery, {
      options: (props) => ({
        variables: {
          id: props.content.fieldFormRawField.first.targetId,
        },
      }),
      skip: (props) => props.data,
    })(withApollo(ParagraphForm))
  )
);
